import React, { useContext, useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { AppContext } from "../context/AppContextProvider";
import useAxiosInstance from "../lib/useAxiosInstance";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";
import "../Styles/constituencyPlaybook.css";
import polstratLogo from "../assets/images/polstrat.png"

/* ─── helpers ────────────────────────────────────────────────────── */
const nc = (x) => {
  if (x === null || x === undefined || String(x).trim() === "" || x === "null") return "—";
  return String(x).trim();
};
const blank = (x) =>
  !x || String(x).trim() === "" || x === "null" || x === null || x === undefined;

const villageName = (v) =>
  v["गाँव_का_नाम"] || v["village_name"] || v["गाँव_या_फ़ाइल_का_नाम"] || "अज्ञात";

/* ─── Party colours ─────────────────────────────────────────────── */
const PARTY_COLORS = {
  भाजपा: "#FF6B1A", BJP: "#FF6B1A",
  सपा: "#FCD34D", SP: "#FCD34D",
  बसपा: "#A78BFA", BSP: "#A78BFA",
  कांग्रेस: "#34D399", INC: "#34D399",
};
const partyColor = (p = "") => {
  for (const [k, v] of Object.entries(PARTY_COLORS)) {
    if (p.toLowerCase().includes(k.toLowerCase())) return v;
  }
  return "#94A3B8";
};

/* ─── Badges ────────────────────────────────────────────────────── */
function LeanBadge({ label }) {
  if (blank(label)) return <span style={{ color: "#94A3B8" }}>—</span>;
  const color = partyColor(label);
  return (
    <span style={{
      display: "inline-block", padding: "3px 10px", borderRadius: 20,
      fontSize: 12, fontWeight: 700, letterSpacing: "0.02em",
      background: color + "22", border: `1px solid ${color}55`, color,
    }}>{label}</span>
  );
}

function EconBadge({ label }) {
  if (blank(label)) return <span style={{ color: "#94A3B8" }}>—</span>;
  const l = label.toLowerCase();
  let color = "#FBBF24";
  if (l.includes("सम्पन्न") || l.includes("उच्च") || l.includes("high")) color = "#34D399";
  else if (l.includes("निम्न") || l.includes("low")) color = "#F87171";
  return (
    <span style={{
      display: "inline-block", padding: "3px 10px", borderRadius: 20,
      fontSize: 12, fontWeight: 700,
      background: color + "22", border: `1px solid ${color}55`, color,
    }}>{label}</span>
  );
}

/* ─── Section header ─────────────────────────────────────────────── */
function SecHead({ icon, title, sub, accent, isDark }) {
  const ac = accent || "#FF6B1A";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
      <div style={{
        width: 40, height: 40, borderRadius: 12, flexShrink: 0,
        background: ac + "22", border: `1px solid ${ac}44`,
        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18,
      }}>{icon}</div>
      <div>
        <div style={{ fontSize: 20, fontWeight: 800, color: isDark ? "#F0F6FF" : "#001D30", lineHeight: 1.2 }}>{title}</div>
        {sub && <div style={{ fontSize: 13, color: isDark ? "rgba(160,185,230,0.7)" : "#6B7280", marginTop: 2 }}>{sub}</div>}
      </div>
      <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, ${ac}44, transparent)`, marginLeft: 8 }} />
    </div>
  );
}

/* ─── Stat card ─────────────────────────────────────────────────── */
function StatCard({ label, value, accent, isDark }) {
  const ac = accent || "#FF6B1A";
  const bg = isDark ? "rgba(255,255,255,0.04)" : "rgba(0,72,119,0.04)";
  const border = isDark ? "rgba(255,255,255,0.09)" : "rgba(0,72,119,0.12)";
  return (
    <div style={{
      background: bg, border: `1px solid ${border}`, borderRadius: 16,
      padding: "18px 20px", flex: 1, minWidth: 90,
      borderTop: `3px solid ${ac}`,
    }}>
      <div style={{ fontSize: 30, fontWeight: 900, color: isDark ? "#F0F6FF" : "#001D30", lineHeight: 1, letterSpacing: "-0.02em" }}>{value}</div>
      <div style={{ fontSize: 12, fontWeight: 700, color: isDark ? "rgba(160,185,230,0.7)" : "#6B7280", marginTop: 6, textTransform: "uppercase", letterSpacing: "0.07em" }}>{label}</div>
    </div>
  );
}

/* ─── Voter donut chart ──────────────────────────────────────────── */
function VoterDonut({ male, female, total, isDark }) {
  const m = parseInt(male) || 0;
  const f = parseInt(female) || 0;
  if (!m && !f) return null;
  const t = parseInt(total) || (m + f);
  const segments = [
    { name: "पुरुष", value: m, color: "#3B82F6", pct: t ? Math.round(m / t * 100) : 0 },
    { name: "महिला", value: f, color: "#F472B6", pct: t ? Math.round(f / t * 100) : 0 },
  ];
  const P = isDark ? "#F0F6FF" : "#001D30";
  const M = isDark ? "rgba(160,185,230,0.65)" : "#6B7280";
  const B = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,72,119,0.1)";
  const textColor = isDark ? "#CBD5E1" : "#374151";
  return (
    <div style={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: 32, alignItems: "center" }}>
      {/* donut */}
      <div style={{ width: 200, height: 200, flexShrink: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={segments} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={3} dataKey="value" strokeWidth={0}>
              {segments.map((d, i) => <Cell key={i} fill={d.color} />)}
            </Pie>
            <Tooltip contentStyle={{ background: isDark ? "#0D1730" : "#fff", border: "1px solid rgba(0,0,0,0.1)", borderRadius: 10, fontSize: 13 }} itemStyle={{ color: textColor }} />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* stats */}
      <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
        {/* total */}
        {t > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 42, fontWeight: 900, color: P, lineHeight: 1, letterSpacing: "-0.03em" }}>{t.toLocaleString("hi-IN")}</div>
            <div style={{ fontSize: 13, color: M, textTransform: "uppercase", letterSpacing: "0.1em", marginTop: 4 }}>कुल मतदाता</div>
          </div>
        )}

        {/* gender rows */}
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          {segments.map((d) => (
            <div key={d.name} style={{
              flex: 1, minWidth: 120,
              background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,72,119,0.03)",
              border: `1px solid ${B}`, borderRadius: 14, padding: "14px 16px",
              borderTop: `3px solid ${d.color}`,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <div style={{ width: 10, height: 10, borderRadius: "50%", background: d.color, boxShadow: `0 0 8px ${d.color}88` }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: d.color, textTransform: "uppercase", letterSpacing: "0.06em" }}>{d.name}</span>
              </div>
              <div style={{ fontSize: 30, fontWeight: 900, color: P, lineHeight: 1, letterSpacing: "-0.02em" }}>{d.value.toLocaleString("hi-IN")}</div>
              {/* bar */}
              <div style={{ marginTop: 10, height: 5, borderRadius: 3, background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)", overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${d.pct}%`, background: d.color, borderRadius: 3, transition: "width 0.6s ease" }} />
              </div>
              <div style={{ fontSize: 12, color: M, marginTop: 4 }}>{d.pct}% हिस्सेदारी</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Caste bar chart ────────────────────────────────────────────── */
function CasteBarChart({ caste, isDark }) {
  const data = caste
    .filter((r) => !blank(r["group"]) && !blank(r["voters"]))
    .map((r) => ({ name: nc(r["group"]), voters: parseInt(r["voters"]) || 0 }))
    .sort((a, b) => b.voters - a.voters)
    .slice(0, 10);
  if (!data.length) return null;
  const textColor = isDark ? "#94A3B8" : "#6B7280";
  const gridColor = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,72,119,0.08)";
  return (
    <ResponsiveContainer width="100%" height={Math.max(200, data.length * 42)}>
      <BarChart data={data} layout="vertical" margin={{ top: 0, right: 20, left: 0, bottom: 0 }}>
        <CartesianGrid horizontal={false} stroke={gridColor} />
        <XAxis type="number" tick={{ fill: textColor, fontSize: 12 }} axisLine={false} tickLine={false} />
        <YAxis type="category" dataKey="name" width={95} tick={{ fill: isDark ? "#CBD5E1" : "#374151", fontSize: 13, fontWeight: 600 }} axisLine={false} tickLine={false} />
        <Tooltip cursor={{ fill: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,72,119,0.04)" }} contentStyle={{ background: isDark ? "#0D1730" : "#fff", border: "1px solid rgba(0,0,0,0.1)", borderRadius: 10, fontSize: 13 }} itemStyle={{ color: textColor }} />
        <Bar dataKey="voters" radius={[0, 8, 8, 0]} name="मतदाता">
          {data.map((_, i) => <Cell key={i} fill={`hsl(${200 + i * 22}, 68%, ${isDark ? 55 : 45}%)`} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

/* ─── Scheme satisfaction chart ──────────────────────────────────── */
function SchemeSatisfactionChart({ schemes, isDark }) {
  const data = schemes
    .filter((s) => !blank(s["scheme"]) && parseInt(s["satisfaction"]) > 0)
    .map((s) => ({
      name: nc(s["scheme"]).length > 20 ? nc(s["scheme"]).slice(0, 20) + "…" : nc(s["scheme"]),
      satisfaction: parseInt(s["satisfaction"]) || 0,
    }));
  if (!data.length) return null;
  const textColor = isDark ? "#94A3B8" : "#6B7280";
  const gridColor = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,72,119,0.08)";
  return (
    <ResponsiveContainer width="100%" height={Math.max(160, data.length * 38)}>
      <BarChart data={data} layout="vertical" margin={{ top: 0, right: 20, left: 0, bottom: 0 }}>
        <CartesianGrid horizontal={false} stroke={gridColor} />
        <XAxis type="number" domain={[0, 5]} tick={{ fill: textColor, fontSize: 12 }} axisLine={false} tickLine={false} />
        <YAxis type="category" dataKey="name" width={125} tick={{ fill: isDark ? "#CBD5E1" : "#374151", fontSize: 12, fontWeight: 600 }} axisLine={false} tickLine={false} />
        <Tooltip cursor={{ fill: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,72,119,0.04)" }} contentStyle={{ background: isDark ? "#0D1730" : "#fff", border: "1px solid rgba(0,0,0,0.1)", borderRadius: 10, fontSize: 13 }} itemStyle={{ color: textColor }} formatter={(v) => [`${v}/5`, "संतुष्टि"]} />
        <Bar dataKey="satisfaction" radius={[0, 8, 8, 0]} name="संतुष्टि">
          {data.map((d, i) => <Cell key={i} fill={`hsl(${d.satisfaction * 24}, 75%, ${isDark ? 55 : 45}%)`} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

/* ─── Candidate party donut ──────────────────────────────────────── */
function CandidatePartyPie({ candidates, isDark }) {
  const counts = {};
  candidates.forEach((c) => {
    const m = nc(c["name"]).match(/\(([^)]+)\)/);
    const party = m ? m[1] : "अन्य";
    counts[party] = (counts[party] || 0) + 1;
  });
  const data = Object.entries(counts).map(([name, value]) => ({ name, value, color: partyColor(name) }));
  if (data.length < 2) return null;
  const textColor = isDark ? "#CBD5E1" : "#374151";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
      <div style={{ width: 150, height: 150, flexShrink: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} cx="50%" cy="50%" outerRadius={65} paddingAngle={4} dataKey="value" strokeWidth={0}>
              {data.map((d, i) => <Cell key={i} fill={d.color} />)}
            </Pie>
            <Tooltip contentStyle={{ background: isDark ? "#0D1730" : "#fff", border: "1px solid rgba(0,0,0,0.1)", borderRadius: 10, fontSize: 13 }} itemStyle={{ color: textColor }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {data.map((d) => (
          <div key={d.name} style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: d.color, boxShadow: `0 0 6px ${d.color}88`, flexShrink: 0 }} />
            <span style={{ fontSize: 15, fontWeight: 700, color: d.color }}>{d.name}</span>
            <span style={{ fontSize: 13, color: isDark ? "rgba(160,185,230,0.7)" : "#6B7280" }}>({d.value})</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Village detail ─────────────────────────────────────────────── */
function VillageDetail({ village, isDark }) {
  const ov = village["overview"] || {};
  const voters = ov["voters"] || {};
  const nm = villageName(village);
  const pradhan = nc(ov["gram_pradhan"]);
  const finding = ov["main_finding"];
  const samm = (ov["sammilit_gram"] || []).filter(Boolean);
  const bnames = (ov["booth_names"] || []).filter(Boolean);

  const caste = (village["caste_social"] || []).filter((r) => !blank(r["group"]));
  const candidates = (village["candidates"] || []).filter((r) => !blank(r["name"]));
  const workers = (village["workers"] || []).filter((r) => !blank(r["name"]));
  const influencers = (village["influencers"] || []).filter((r) => !blank(r["name"]));
  const issues = (village["issues"] || []).filter((r) => !blank(r["issue"]));
  const hotspots = (village["hotspots"] || []).filter((r) => !blank(r["place"]));
  const schemes = (village["schemes"] || []).filter((r) => !blank(r["scheme"]));
  const partyAssessment = (village["party_assessment"] || []).filter((r) => !blank(r["party"]));
  const stratRaw = village["strategic_recommendations"];
  const stratText = Array.isArray(stratRaw) ? stratRaw.join("\n\n") : typeof stratRaw === "string" ? stratRaw : "";

  const P = isDark ? "#F0F6FF" : "#001D30";
  const S = isDark ? "rgba(200,218,255,0.78)" : "#374151";
  const M = isDark ? "rgba(150,175,220,0.55)" : "#6B7280";
  const B = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,72,119,0.1)";
  const card = {
    background: isDark ? "rgba(255,255,255,0.03)" : "#fff",
    border: `1px solid ${B}`, borderRadius: 20,
    padding: "24px 28px", marginBottom: 20,
  };

  return (
    <div style={{ paddingBottom: 60 }}>

      {/* ── HERO ── */}
      <div style={{
        background: isDark
          ? "linear-gradient(135deg,rgba(0,40,80,0.96) 0%,rgba(0,18,42,0.99) 100%)"
          : "linear-gradient(135deg,#EFF6FF 0%,#F0FDF4 100%)",
        border: `1px solid ${B}`, borderRadius: 24,
        padding: "32px 32px 28px", marginBottom: 20,
        position: "relative", overflow: "hidden",
      }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: "linear-gradient(90deg,#FF6B1A,#FF3F88,#8B5CF6)" }} />
        <div style={{ position: "absolute", right: -120, top: -120, width: 350, height: 350, borderRadius: "50%", background: "radial-gradient(circle,rgba(255,107,26,0.1) 0%,transparent 65%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", left: -60, bottom: -60, width: 220, height: 220, borderRadius: "50%", background: "radial-gradient(circle,rgba(99,102,241,0.08) 0%,transparent 65%)", pointerEvents: "none" }} />

        <div style={{ position: "relative", zIndex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--saffron)", marginBottom: 8 }}>
            PIU — ग्राम गुणात्मक सर्वेक्षण
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 20, marginBottom: 24 }}>
            <div>
              <h1 style={{ fontSize: 44, fontWeight: 900, color: P, margin: 0, lineHeight: 1.05, letterSpacing: "-0.03em" }}>{nm}</h1>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 10 }}>
                {!blank(ov["gram_panchayat"]) && <span style={{ fontSize: 14, color: S }}>ग्रा.पं. <strong style={{ color: P }}>{ov["gram_panchayat"]}</strong></span>}
                {!blank(ov["block"]) && <span style={{ fontSize: 14, color: M }}>· ब्लॉक <strong style={{ color: S }}>{ov["block"]}</strong></span>}
                {!blank(ov["nyay_panchayat"]) && <span style={{ fontSize: 14, color: M }}>· {ov["nyay_panchayat"]}</span>}
              </div>
            </div>

            {!blank(pradhan) && (
              <div style={{
                display: "flex", alignItems: "center", gap: 12,
                background: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,72,119,0.07)",
                border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,72,119,0.14)"}`,
                borderRadius: 16, padding: "12px 18px", flexShrink: 0,
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: "50%",
                  background: "linear-gradient(135deg,#FF6B1A,#FF3F88)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontWeight: 800, fontSize: 16, color: "#fff", flexShrink: 0,
                }}>{pradhan[0] || "?"}</div>
                <div>
                  <div style={{ fontSize: 11, color: M, textTransform: "uppercase", letterSpacing: "0.08em" }}>ग्राम प्रधान</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: P }}>{pradhan}</div>
                </div>
              </div>
            )}
          </div>

          {[voters["total"], voters["female"], voters["male"], ov["booth_count"]].some((v) => !blank(v)) && (
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
              {!blank(voters["total"]) && <StatCard label="कुल मतदाता" value={parseInt(voters["total"]).toLocaleString("hi-IN")} accent="#3B82F6" isDark={isDark} />}
              {!blank(voters["male"]) && <StatCard label="पुरुष" value={parseInt(voters["male"]).toLocaleString("hi-IN")} accent="#60A5FA" isDark={isDark} />}
              {!blank(voters["female"]) && <StatCard label="महिला" value={parseInt(voters["female"]).toLocaleString("hi-IN")} accent="#F472B6" isDark={isDark} />}
              {!blank(ov["booth_count"]) && <StatCard label="बूथ" value={nc(ov["booth_count"])} accent="#FF6B1A" isDark={isDark} />}
            </div>
          )}

          {!blank(finding) && (
            <div style={{
              background: "linear-gradient(135deg,rgba(255,107,26,0.12),rgba(255,63,136,0.06))",
              border: "1px solid rgba(255,107,26,0.25)", borderRadius: 14, padding: "14px 18px",
              fontSize: 15, color: isDark ? "rgba(255,215,185,0.92)" : "#92400e", lineHeight: 1.75,
              display: "flex", gap: 12, alignItems: "flex-start",
            }}>
              <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>🔍</span>
              <span>{finding}</span>
            </div>
          )}
        </div>
      </div>

      {/* ── VOTER DISTRIBUTION ── */}
      {(!blank(voters["male"]) || !blank(voters["female"])) && (
        <div style={card}>
          <SecHead icon="🗳️" title="मतदाता वितरण" sub="Voter gender distribution" isDark={isDark} accent="#3B82F6" />
          <VoterDonut male={voters["male"]} female={voters["female"]} total={voters["total"]} isDark={isDark} />
          {(samm.length > 0 || bnames.length > 0) && (
            <div style={{ marginTop: 20, display: "flex", flexWrap: "wrap", gap: 16, paddingTop: 16, borderTop: `1px solid ${B}` }}>
              {samm.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, color: M, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>सम्मिलित ग्राम</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {samm.map((s, i) => (
                      <span key={i} style={{ fontSize: 13, padding: "4px 12px", borderRadius: 20, background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,72,119,0.06)", color: S, border: `1px solid ${B}` }}>{s}</span>
                    ))}
                  </div>
                </div>
              )}
              {bnames.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, color: M, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>बूथ स्थल</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {bnames.map((b, i) => (
                      <span key={i} style={{ fontSize: 13, padding: "4px 12px", borderRadius: 20, background: "rgba(255,107,26,0.08)", color: "var(--saffron)", border: "1px solid rgba(255,107,26,0.2)" }}>{b}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── PARICHAY ── */}
      {!blank(village["parichay"]) && (
        <div style={card}>
          <SecHead icon="📖" title="परिचय" sub="Political landscape overview" isDark={isDark} />
          <div style={{ borderLeft: "4px solid var(--saffron)", paddingLeft: 20, fontSize: 15, color: S, lineHeight: 1.95 }}>
            {village["parichay"]}
          </div>
        </div>
      )}

      {/* ── CASTE ── */}
      {caste.length > 0 && (
        <div style={card}>
          <SecHead icon="👥" title="जातिगत व सामाजिक संरचना" sub="Caste & social breakdown" isDark={isDark} accent="#8B5CF6" />
          {caste.some((r) => !blank(r["voters"])) && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: M, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>मतदाता संख्या (जातिवार)</div>
              <CasteBarChart caste={caste} isDark={isDark} />
            </div>
          )}
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {["जाति / समूह", "घर", "वोटर", "राजनीतिक झुकाव", "आर्थिक स्थिति"].map((h) => (
                    <th key={h} style={{ padding: "10px 12px", fontSize: 11, fontWeight: 700, color: M, textTransform: "uppercase", letterSpacing: "0.08em", textAlign: "left", borderBottom: `2px solid ${B}`, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {caste.map((r, i) => (
                  <tr key={i} style={{ borderBottom: i < caste.length - 1 ? `1px solid ${B}` : "none" }}>
                    <td style={{ padding: "12px 12px", fontWeight: 700, fontSize: 15, color: P }}>{nc(r["group"])}</td>
                    <td style={{ padding: "12px 12px", fontSize: 14, color: S, textAlign: "center" }}>{nc(r["houses"])}</td>
                    <td style={{ padding: "12px 12px", fontWeight: 700, fontSize: 15, color: P, textAlign: "center" }}>{nc(r["voters"])}</td>
                    <td style={{ padding: "12px 12px" }}><LeanBadge label={r["political_lean"] || ""} /></td>
                    <td style={{ padding: "12px 12px" }}><EconBadge label={r["economic"] || ""} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── CANDIDATES ── */}
      {candidates.length > 0 && (
        <div style={card}>
          <SecHead icon="🏛️" title="उम्मीदवार" sub="Candidate influence matrix" isDark={isDark} accent="#F59E0B" />
          {candidates.length > 1 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: M, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>दलीय वितरण</div>
              <CandidatePartyPie candidates={candidates} isDark={isDark} />
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 12 }}>
            {candidates.map((r, i) => {
              const nameRaw = nc(r["name"]);
              const partyMatch = nameRaw.match(/\(([^)]+)\)/);
              const cleanName = partyMatch ? nameRaw.replace(/\s*\([^)]+\)/, "").trim() : nameRaw;
              const party = partyMatch ? partyMatch[1] : "";
              const pc = partyColor(party);
              return (
                <div key={i} style={{ background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,72,119,0.03)", border: `1px solid ${B}`, borderRadius: 16, padding: "16px 18px", borderLeft: `3px solid ${pc}` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                    <div style={{ width: 36, height: 36, borderRadius: "50%", flexShrink: 0, background: pc + "33", border: `2px solid ${pc}55`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 800, color: pc }}>{cleanName[0] || "?"}</div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: P, lineHeight: 1.2 }}>{cleanName}</div>
                      {party && <LeanBadge label={party} />}
                    </div>
                  </div>
                  {!blank(r["influence"]) && <div style={{ fontSize: 13, color: S, lineHeight: 1.65, paddingTop: 8, borderTop: `1px solid ${B}` }}>{nc(r["influence"])}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── WORKERS ── */}
      {workers.length > 0 && (
        <div style={card}>
          <SecHead icon="🤝" title="प्रमुख कार्यकर्ता" sub="Key party workers" isDark={isDark} accent="#34D399" />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 12 }}>
            {workers.map((w, i) => (
              <div key={i} style={{ background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,72,119,0.03)", border: `1px solid ${B}`, borderRadius: 14, padding: "14px 16px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: "50%", background: "linear-gradient(135deg,#34D399,#06B6D4)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 14, color: "#fff", flexShrink: 0 }}>{(nc(w["name"]))[0]}</div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: P, lineHeight: 1.2 }}>{nc(w["name"])}</div>
                    {!blank(w["caste"]) && <div style={{ fontSize: 12, color: M, marginTop: 2 }}>{w["caste"]}</div>}
                  </div>
                </div>
                {!blank(w["phone"]) && <a href={`tel:${w["phone"]}`} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: "#3B82F6", textDecoration: "none", marginBottom: 6 }}>📞 {w["phone"]}</a>}
                {!blank(w["party"]) && <LeanBadge label={w["party"]} />}
                {!blank(w["note"]) && <div style={{ fontSize: 13, color: S, lineHeight: 1.6, marginTop: 8 }}>{w["note"]}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── INFLUENCERS ── */}
      {influencers.length > 0 && (
        <div style={card}>
          <SecHead icon="💡" title="प्रभावशाली व्यक्ति" sub="Non-political community influencers" isDark={isDark} accent="#A78BFA" />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 12 }}>
            {influencers.map((r, i) => (
              <div key={i} style={{ background: isDark ? "rgba(167,139,250,0.06)" : "rgba(167,139,250,0.05)", border: "1px solid rgba(167,139,250,0.15)", borderRadius: 14, padding: "14px 16px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: "50%", flexShrink: 0, background: "linear-gradient(135deg,#8B5CF6,#EC4899)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 14, color: "#fff" }}>{(nc(r["name"]))[0]}</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: P }}>{nc(r["name"])}</div>
                </div>
                {!blank(r["profession"]) && <div style={{ fontSize: 13, color: S, marginBottom: 4 }}>💼 {r["profession"]}</div>}
                {!blank(r["contact"]) && <div style={{ fontSize: 13, color: "#60A5FA", marginBottom: 4 }}>📞 {r["contact"]}</div>}
                {!blank(r["address"]) && <div style={{ fontSize: 13, color: M, marginBottom: 4 }}>📍 {r["address"]}</div>}
                {!blank(r["reason"]) && <div style={{ fontSize: 13, color: S, lineHeight: 1.65, marginTop: 8, paddingTop: 8, borderTop: `1px solid ${B}` }}>{r["reason"]}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── ISSUES ── */}
      {issues.length > 0 && (
        <div style={card}>
          <SecHead icon="⚠️" title="प्रमुख मुद्दे" sub="Key issues & public sentiment" isDark={isDark} accent="#F87171" />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 12 }}>
            {issues.map((r, i) => (
              <div key={i} style={{ display: "flex", gap: 14, alignItems: "flex-start", background: isDark ? "rgba(248,113,113,0.05)" : "rgba(248,113,113,0.04)", border: "1px solid rgba(248,113,113,0.15)", borderRadius: 14, padding: "14px 16px" }}>
                <div style={{ width: 32, height: 32, borderRadius: 10, flexShrink: 0, background: "linear-gradient(135deg,#FF6B1A,#EF4444)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13, fontWeight: 800 }}>{nc(r["sr"]) || String(i + 1)}</div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: P, marginBottom: 4 }}>{nc(r["issue"])}</div>
                  {!blank(r["desc"]) && <div style={{ fontSize: 13, color: S, lineHeight: 1.7 }}>{r["desc"]}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── HOTSPOTS ── */}
      {hotspots.length > 0 && (
        <div style={card}>
          <SecHead icon="📍" title="हॉटस्पॉट" sub="Campaign branding locations" isDark={isDark} />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(170px,1fr))", gap: 12 }}>
            {hotspots.map((h, i) => (
              <div key={i} style={{ borderRadius: 14, overflow: "hidden", background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,72,119,0.03)", border: `1px solid ${B}` }}>
                <div style={{ height: 4, background: "linear-gradient(90deg,#FF6B1A,#FF3F88)" }} />
                <div style={{ padding: "12px 14px" }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: P, marginBottom: 4 }}>{nc(h["place"])}</div>
                  {!blank(h["type"]) && <div style={{ fontSize: 12, color: M, marginBottom: 8 }}>{h["type"]}</div>}
                  {!blank(h["branding"]) && <span style={{ fontSize: 11, fontWeight: 700, background: "rgba(255,107,26,0.14)", color: "var(--saffron)", border: "1px solid rgba(255,107,26,0.25)", borderRadius: 6, padding: "3px 10px", display: "inline-block" }}>{h["branding"]}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── SCHEMES ── */}
      {schemes.length > 0 && (
        <div style={card}>
          <SecHead icon="📋" title="सरकारी योजनाएं" sub="Scheme coverage & satisfaction" isDark={isDark} accent="#06B6D4" />
          {schemes.some((s) => parseInt(s["satisfaction"]) > 0) && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: M, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>संतुष्टि स्तर (1–5)</div>
              <SchemeSatisfactionChart schemes={schemes} isDark={isDark} />
            </div>
          )}
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {["योजना", "स्तर", "लाभ", "कठिनाई", "संतुष्टि"].map((h) => (
                    <th key={h} style={{ padding: "10px 12px", fontSize: 11, fontWeight: 700, color: M, textTransform: "uppercase", letterSpacing: "0.06em", textAlign: "left", borderBottom: `2px solid ${B}`, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {schemes.map((s, i) => {
                  const sat = parseInt(s["satisfaction"]) || 0;
                  const diff = nc(s["difficulty"]);
                  const dl = diff.toLowerCase();
                  const dc = (dl === "हाँ" || dl === "yes") ? "#F87171" : (dl === "नहीं" || dl === "no") ? "#34D399" : "#FBBF24";
                  return (
                    <tr key={i} style={{ borderBottom: i < schemes.length - 1 ? `1px solid ${B}` : "none" }}>
                      <td style={{ padding: "12px 12px", fontWeight: 700, fontSize: 15, color: P }}>{nc(s["scheme"])}</td>
                      <td style={{ padding: "12px 12px", fontSize: 14, color: S }}>{nc(s["level"])}</td>
                      <td style={{ padding: "12px 12px", fontSize: 14, color: S }}>{nc(s["coverage"])}</td>
                      <td style={{ padding: "12px 12px" }}><span style={{ fontSize: 13, fontWeight: 700, color: dc }}>{diff}</span></td>
                      <td style={{ padding: "12px 12px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ width: 60, height: 6, borderRadius: 4, background: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,72,119,0.1)", overflow: "hidden" }}>
                            <div style={{ width: `${(sat / 5) * 100}%`, height: "100%", borderRadius: 4, background: `hsl(${sat * 24}, 75%, 55%)` }} />
                          </div>
                          <span style={{ fontSize: 13, fontWeight: 700, color: P }}>{sat}/5</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── PARTY ASSESSMENT ── */}
      {partyAssessment.length > 0 && (
        <div style={card}>
          <SecHead icon="🏢" title="दल आकलन" sub="Party organisational position" isDark={isDark} accent="#F59E0B" />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 12 }}>
            {partyAssessment.map((r, i) => {
              const dot = partyColor(r["party"]);
              return (
                <div key={i} style={{ background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,72,119,0.03)", border: `1px solid ${B}`, borderRadius: 16, padding: "16px 18px", borderLeft: `3px solid ${dot}` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                    <div style={{ width: 12, height: 12, borderRadius: "50%", background: dot, boxShadow: `0 0 10px ${dot}99`, flexShrink: 0 }} />
                    <span style={{ fontSize: 17, fontWeight: 800, color: P }}>{nc(r["party"])}</span>
                  </div>
                  <div style={{ fontSize: 14, color: S, lineHeight: 1.75 }}>{nc(r["status"])}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── STRATEGY ── */}
      {!blank(stratText) && (
        <div style={{
          background: isDark ? "rgba(22,34,80,0.7)" : "rgba(220,228,255,0.6)",
          border: "1px solid rgba(99,102,241,0.25)",
          borderRadius: 20, padding: "28px 32px",
          position: "relative", overflow: "hidden", marginBottom: 20,
        }}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: "linear-gradient(90deg,#6366F1,#8B5CF6,#FF6B1A)" }} />
          <SecHead icon="🎯" title="रणनीतिक सुझाव" sub="Strategic recommendations" isDark={isDark} accent="#6366F1" />
          <div style={{ fontSize: 15, color: isDark ? "rgba(205,218,255,0.9)" : "#2A3F72", lineHeight: 2.0, whiteSpace: "pre-wrap" }}>{stratText}</div>
        </div>
      )}
    </div>
  );
}

/* ─── Constituency view ──────────────────────────────────────────── */
function ConstituencyView({ data, isDark }) {
  const d = data?.data || data || {};
  const P = isDark ? "#F0F6FF" : "#001D30";
  const S = isDark ? "rgba(200,218,255,0.78)" : "#374151";
  const M = isDark ? "rgba(150,175,220,0.55)" : "#6B7280";
  const B = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,72,119,0.1)";
  const card = { background: isDark ? "rgba(255,255,255,0.03)" : "#fff", border: `1px solid ${B}`, borderRadius: 20, padding: "24px 28px", marginBottom: 20 };

  const partyPositions = (d.party_positions || []).filter((p) => !blank(p.party));
  const socialCaste = (d.social_caste || []).filter((r) => !blank(r.community) && r.community.length < 80);
  const mlaPerf = (d.mla_performance || []).filter((r) => !blank(r.category));
  const candidatesBjp = (d.candidates_bjp || []).filter((c) => !blank(c.name));
  const candidatesSp = (d.candidates_sp || []).filter((c) => !blank(c.name));
  const swot = (d.swot || []).filter((s) => !blank(s.name));
  const issues = (d.issues || []).filter((i) => !blank(i.category));
  const workers = (d.workers || []).filter((w) => !blank(w.name));
  const electionResults = (d.election_history?.results || []).filter((r) => !blank(r.candidate));
  const boothAnalysis = (d.booth_analysis || []).filter((b) => !blank(b.party));
  const overview = d.overview || {};

  return (
    <div style={{ paddingBottom: 60 }}>
      {/* ── HERO ── */}
      <div style={{ background: isDark ? "linear-gradient(135deg,rgba(0,40,80,0.96),rgba(0,18,42,0.99))" : "linear-gradient(135deg,#EFF6FF,#F0FDF4)", border: `1px solid ${B}`, borderRadius: 24, padding: "32px 32px 28px", marginBottom: 20, position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: "linear-gradient(90deg,#FF6B1A,#FF3F88,#8B5CF6)" }} />
        <div style={{ position: "absolute", right: -100, top: -100, width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle,rgba(255,107,26,0.1) 0%,transparent 65%)", pointerEvents: "none" }} />
        <div style={{ position: "relative", zIndex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--saffron)", marginBottom: 8 }}>PIU — विधानसभा गुणात्मक सर्वेक्षण</div>
          <h1 style={{ fontSize: 46, fontWeight: 900, color: P, margin: "0 0 6px", lineHeight: 1.05, letterSpacing: "-0.03em" }}>{nc(d.ac_name)}</h1>
          <div style={{ fontSize: 15, color: S, marginBottom: 20 }}>{nc(d.district)} · {nc(d.parliamentary)}</div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {!blank(d.mla) && (
              <div style={{ background: isDark ? "rgba(255,107,26,0.1)" : "rgba(255,107,26,0.08)", border: "1px solid rgba(255,107,26,0.25)", borderRadius: 12, padding: "10px 16px" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "var(--saffron)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 3 }}>विधायक</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: P, whiteSpace: "pre-line" }}>{d.mla}</div>
              </div>
            )}
            {!blank(d.mp) && (
              <div style={{ background: isDark ? "rgba(99,102,241,0.1)" : "rgba(99,102,241,0.06)", border: "1px solid rgba(99,102,241,0.2)", borderRadius: 12, padding: "10px 16px" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#6366F1", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 3 }}>सांसद</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: P, whiteSpace: "pre-line" }}>{d.mp}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── OVERVIEW ── */}
      {Object.keys(overview).length > 0 && (
        <div style={card}>
          <SecHead icon="📊" title="निर्वाचन क्षेत्र अवलोकन" sub="Constituency overview" isDark={isDark} accent="#3B82F6" />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 0 }}>
            {Object.entries(overview).map(([k, v], i) => (
              <div key={i} style={{ display: "flex", flexDirection: "column", padding: "12px 0", borderBottom: `1px solid ${B}`, paddingRight: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: M, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>{k}</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: P, lineHeight: 1.6, whiteSpace: "pre-line" }}>{nc(v)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── PARTY POSITIONS ── */}
      {partyPositions.length > 0 && (
        <div style={card}>
          <SecHead icon="🏢" title="दलीय स्थिति" sub="Party organizational positions" isDark={isDark} accent="#F59E0B" />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 16 }}>
            {partyPositions.map((p, i) => {
              const pc = partyColor(p.party);
              return (
                <div key={i} style={{ background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,72,119,0.03)", border: `1px solid ${B}`, borderRadius: 16, padding: "18px 20px", borderLeft: `3px solid ${pc}` }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                    <div style={{ width: 12, height: 12, borderRadius: "50%", background: pc, boxShadow: `0 0 10px ${pc}99` }} />
                    <span style={{ fontSize: 16, fontWeight: 800, color: P }}>{nc(p.party)}</span>
                  </div>
                  {!blank(p.current_status) && <div style={{ fontSize: 13, color: S, lineHeight: 1.75, marginBottom: 10, paddingBottom: 10, borderBottom: `1px solid ${B}` }}>{p.current_status}</div>}
                  {!blank(p.social_base) && <div style={{ fontSize: 13, color: S, lineHeight: 1.7, marginBottom: 6 }}><span style={{ fontWeight: 700, color: M }}>सामाजिक आधार: </span>{p.social_base}</div>}
                  {!blank(p.public_sentiment) && <div style={{ fontSize: 13, color: S, lineHeight: 1.7, marginBottom: 6 }}><span style={{ fontWeight: 700, color: M }}>जनभावना: </span>{p.public_sentiment}</div>}
                  {!blank(p.outlook_2027) && <div style={{ fontSize: 13, color: isDark ? "rgba(250,220,100,0.85)" : "#92400e", lineHeight: 1.7, background: isDark ? "rgba(250,220,100,0.06)" : "rgba(251,191,36,0.08)", borderRadius: 8, padding: "8px 10px", marginTop: 8 }}>🔮 {p.outlook_2027}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── SOCIAL CASTE ── */}
      {socialCaste.length > 0 && (
        <div style={card}>
          <SecHead icon="👥" title="सामाजिक व जातिगत संरचना" sub="Social & caste distribution" isDark={isDark} accent="#8B5CF6" />
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {["समुदाय", "AC में स्थान", "राजनीतिक झुकाव", "विशेष टिप्पणी"].map((h) => (
                    <th key={h} style={{ padding: "10px 12px", fontSize: 11, fontWeight: 700, color: M, textTransform: "uppercase", letterSpacing: "0.07em", textAlign: "left", borderBottom: `2px solid ${B}`, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {socialCaste.map((r, i) => (
                  <tr key={i} style={{ borderBottom: i < socialCaste.length - 1 ? `1px solid ${B}` : "none" }}>
                    <td style={{ padding: "12px 12px", fontWeight: 700, fontSize: 15, color: P, whiteSpace: "nowrap" }}>{nc(r.community)}</td>
                    <td style={{ padding: "12px 12px", fontSize: 13, color: S }}>{nc(r.ac_position)}</td>
                    <td style={{ padding: "12px 12px" }}><LeanBadge label={r.political_lean || ""} /></td>
                    <td style={{ padding: "12px 12px", fontSize: 13, color: S, lineHeight: 1.65, maxWidth: 320 }}>{nc(r.role_notes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── MLA PERFORMANCE ── */}
      {mlaPerf.length > 0 && (
        <div style={card}>
          <SecHead icon="📈" title="विधायक प्रदर्शन आकलन" sub="MLA performance assessment" isDark={isDark} accent="#34D399" />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 12 }}>
            {mlaPerf.map((r, i) => {
              const rating = nc(r.rating).toLowerCase();
              const ratingColor = rating.includes("अत्यंत") || rating.includes("उच्च") ? "#34D399" : rating.includes("न्यूनतम") || rating.includes("नकारात्मक") ? "#F87171" : "#FBBF24";
              return (
                <div key={i} style={{ background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,72,119,0.03)", border: `1px solid ${B}`, borderRadius: 16, padding: "16px 18px", borderTop: `3px solid ${ratingColor}` }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: P, marginBottom: 6 }}>{nc(r.category)}</div>
                  <div style={{ display: "inline-block", fontSize: 12, fontWeight: 700, color: ratingColor, background: ratingColor + "18", border: `1px solid ${ratingColor}44`, borderRadius: 20, padding: "3px 10px", marginBottom: 10 }}>{nc(r.rating)}</div>
                  {!blank(r.notes) && <div style={{ fontSize: 13, color: S, lineHeight: 1.75 }}>{r.notes}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── CANDIDATES ── */}
      {(candidatesBjp.length > 0 || candidatesSp.length > 0) && (
        <div style={card}>
          <SecHead icon="🏛️" title="संभावित उम्मीदवार" sub="Candidate profiles by party" isDark={isDark} accent="#F59E0B" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            {[{ label: "भाजपा उम्मीदवार", list: candidatesBjp, color: "#FF6B1A" }, { label: "सपा उम्मीदवार", list: candidatesSp, color: "#FCD34D" }].map(({ label, list, color }) => list.length > 0 && (
              <div key={label}>
                <div style={{ fontSize: 13, fontWeight: 800, color, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: color, boxShadow: `0 0 8px ${color}99` }} />{label}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {list.map((c, i) => (
                    <div key={i} style={{ background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,72,119,0.03)", border: `1px solid ${B}`, borderRadius: 14, padding: "14px 16px", borderLeft: `3px solid ${color}` }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: P, marginBottom: 6 }}>{nc(c.name)}</div>
                      {!blank(c.profile) && <div style={{ fontSize: 13, color: S, lineHeight: 1.7, marginBottom: 8 }}>{c.profile}</div>}
                      {!blank(c.public_support) && <div style={{ fontSize: 12, color: isDark ? "rgba(250,220,100,0.85)" : "#92400e", lineHeight: 1.65, background: isDark ? "rgba(250,220,100,0.06)" : "rgba(251,191,36,0.08)", borderRadius: 8, padding: "8px 10px" }}>🎯 {c.public_support}</div>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── SWOT ── */}
      {swot.length > 0 && (
        <div style={card}>
          <SecHead icon="⚖️" title="SWOT विश्लेषण" sub="Candidate strength-weakness analysis" isDark={isDark} accent="#A78BFA" />
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {swot.map((s, i) => (
              <div key={i} style={{ background: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,72,119,0.02)", border: `1px solid ${B}`, borderRadius: 16, overflow: "hidden" }}>
                <div style={{ background: isDark ? "rgba(167,139,250,0.1)" : "rgba(167,139,250,0.08)", padding: "12px 20px", borderBottom: `1px solid ${B}` }}>
                  <span style={{ fontSize: 16, fontWeight: 800, color: P }}>{nc(s.full_header || s.name)}</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 0 }}>
                  {[
                    { label: "💪 ताकत", key: "strengths", color: "#34D399" },
                    { label: "⚠️ कमजोरी", key: "weaknesses", color: "#F87171" },
                    { label: "🌟 अवसर", key: "opportunities", color: "#60A5FA" },
                    { label: "🔴 खतरे", key: "threats", color: "#FBBF24" },
                  ].map(({ label, key, color }) => !blank(s[key]) && (
                    <div key={key} style={{ padding: "14px 18px", borderRight: `1px solid ${B}`, borderBottom: `1px solid ${B}` }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color, marginBottom: 8, letterSpacing: "0.04em" }}>{label}</div>
                      <div style={{ fontSize: 13, color: S, lineHeight: 1.75 }}>{s[key]}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── ISSUES ── */}
      {issues.length > 0 && (
        <div style={card}>
          <SecHead icon="⚠️" title="प्रमुख मुद्दे" sub="Key constituency issues" isDark={isDark} accent="#F87171" />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 12 }}>
            {issues.map((r, i) => (
              <div key={i} style={{ background: isDark ? "rgba(248,113,113,0.05)" : "rgba(248,113,113,0.04)", border: "1px solid rgba(248,113,113,0.15)", borderRadius: 14, padding: "16px 18px" }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: P, marginBottom: 4 }}>{nc(r.category)}</div>
                {!blank(r.key_problems) && <div style={{ fontSize: 13, fontWeight: 600, color: "#F87171", marginBottom: 8 }}>📌 {r.key_problems}</div>}
                {!blank(r.detail) && <div style={{ fontSize: 13, color: S, lineHeight: 1.75 }}>{r.detail}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── WORKERS ── */}
      {workers.length > 0 && (
        <div style={card}>
          <SecHead icon="🤝" title="प्रमुख कार्यकर्ता" sub="Key party workers" isDark={isDark} accent="#34D399" />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 10 }}>
            {workers.map((w, i) => {
              const pc = partyColor(w.party || "");
              return (
                <div key={i} style={{ background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,72,119,0.03)", border: `1px solid ${B}`, borderRadius: 14, padding: "14px 16px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                    <div style={{ width: 34, height: 34, borderRadius: "50%", background: pc + "44", border: `2px solid ${pc}66`, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 13, color: pc, flexShrink: 0 }}>{(nc(w.name))[0]}</div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: P, lineHeight: 1.2 }}>{nc(w.name)}</div>
                      {!blank(w.caste) && <div style={{ fontSize: 11, color: M }}>{w.caste}</div>}
                    </div>
                  </div>
                  {!blank(w.phone) && <a href={`tel:${w.phone}`} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: "#3B82F6", textDecoration: "none", marginBottom: 6 }}>📞 {w.phone}</a>}
                  {!blank(w.party) && <LeanBadge label={w.party} />}
                  {!blank(w.note) && <div style={{ fontSize: 12, color: S, lineHeight: 1.6, marginTop: 8 }}>{w.note}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── ELECTION HISTORY ── */}
      {(electionResults.length > 0 || d.election_history?.meta) && (
        <div style={card}>
          <SecHead icon="🗳️" title="चुनाव इतिहास" sub="Election results history" isDark={isDark} accent="#06B6D4" />
          {d.election_history?.meta && (
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
              {[["कुल मतदाता", d.election_history.meta.total_voters], ["मतदान प्रतिशत", d.election_history.meta.voter_turnout], ["जीत का अंतर", d.election_history.meta.margin]].filter(([, v]) => !blank(v)).map(([label, val]) => (
                <div key={label} style={{ background: isDark ? "rgba(6,182,212,0.08)" : "rgba(6,182,212,0.06)", border: "1px solid rgba(6,182,212,0.2)", borderRadius: 14, padding: "12px 18px", borderTop: "3px solid #06B6D4" }}>
                  <div style={{ fontSize: 22, fontWeight: 900, color: P, lineHeight: 1 }}>{val}</div>
                  <div style={{ fontSize: 12, color: M, textTransform: "uppercase", letterSpacing: "0.07em", marginTop: 4 }}>{label}</div>
                </div>
              ))}
            </div>
          )}
          {electionResults.length > 0 && (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>{["#", "पार्टी", "उम्मीदवार", "मत", "मत%"].map((h) => <th key={h} style={{ padding: "10px 12px", fontSize: 11, fontWeight: 700, color: M, textTransform: "uppercase", textAlign: "left", borderBottom: `2px solid ${B}`, whiteSpace: "nowrap" }}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {electionResults.map((r, i) => {
                    const pc = partyColor(r.party || "");
                    return (
                      <tr key={i} style={{ borderBottom: i < electionResults.length - 1 ? `1px solid ${B}` : "none" }}>
                        <td style={{ padding: "10px 12px", fontSize: 14, fontWeight: 700, color: r.rank === "1" ? "#34D399" : M }}>{r.rank}</td>
                        <td style={{ padding: "10px 12px" }}><LeanBadge label={r.party || ""} /></td>
                        <td style={{ padding: "10px 12px", fontSize: 14, fontWeight: 600, color: P }}>{nc(r.candidate)}</td>
                        <td style={{ padding: "10px 12px", fontSize: 14, fontWeight: 700, color: pc }}>{nc(r.votes)}</td>
                        <td style={{ padding: "10px 12px", fontSize: 13, color: S }}>{nc(r.vote_share)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── BOOTH ANALYSIS ── */}
      {boothAnalysis.length > 0 && (
        <div style={card}>
          <SecHead icon="📊" title="बूथ विश्लेषण" sub="Booth-wise party strength" isDark={isDark} accent="#F59E0B" />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 12 }}>
            {boothAnalysis.map((b, i) => {
              const pc = partyColor(b.party || "");
              return (
                <div key={i} style={{ background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,72,119,0.03)", border: `1px solid ${B}`, borderRadius: 14, padding: "14px 16px", borderLeft: `3px solid ${pc}` }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: P, marginBottom: 10 }}>{nc(b.party)}</div>
                  {[["सुरक्षित", b.secure, "#34D399"], ["अनुकूल", b.favorable, "#60A5FA"], ["रणभूमि", b.battleground, "#FBBF24"], ["कठिन", b.difficult, "#F87171"], ["अत्यंत कठिन", b.very_difficult, "#EF4444"]].filter(([, v]) => !blank(v) && v !== "").map(([lbl, val, col]) => (
                    <div key={lbl} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                      <span style={{ fontSize: 12, color: M }}>{lbl}</span>
                      <span style={{ fontSize: 14, fontWeight: 700, color: col }}>{val}</span>
                    </div>
                  ))}
                  <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${B}`, display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 11, color: M }}>कुल बूथ</span>
                    <span style={{ fontSize: 14, fontWeight: 800, color: P }}>{nc(b.total_booths)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── POLITICAL LANDSCAPE ── */}
      {!blank(d.political_landscape) && (
        <div style={{ ...card, background: isDark ? "rgba(15,25,50,0.6)" : "rgba(239,246,255,0.8)" }}>
          <SecHead icon="🗺️" title="राजनीतिक परिदृश्य" sub="Political landscape analysis" isDark={isDark} accent="#6366F1" />
          <div style={{ fontSize: 15, color: S, lineHeight: 2.0, whiteSpace: "pre-line" }}>{d.political_landscape}</div>
        </div>
      )}

      {/* ── CONCLUSION ── */}
      {!blank(d.conclusion) && (
        <div style={{ background: isDark ? "rgba(22,34,80,0.7)" : "rgba(220,228,255,0.6)", border: "1px solid rgba(99,102,241,0.25)", borderRadius: 20, padding: "28px 32px", position: "relative", overflow: "hidden", marginBottom: 20 }}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: "linear-gradient(90deg,#6366F1,#8B5CF6,#FF6B1A)" }} />
          <SecHead icon="🎯" title="निष्कर्ष एवं रणनीतिक सुझाव" sub="Conclusion & strategic recommendations" isDark={isDark} accent="#6366F1" />
          <div style={{ fontSize: 15, color: isDark ? "rgba(205,218,255,0.9)" : "#2A3F72", lineHeight: 2.0, whiteSpace: "pre-line" }}>{d.conclusion}</div>
        </div>
      )}
    </div>
  );
}

/* ─── Main modal ─────────────────────────────────────────────────── */
export default function ConstituencyPlaybookModal({ open, onClose }) {
  const { themes } = useContext(AppContext);
  const isDark = themes === "darkMode";
  const axiosInstance = useAxiosInstance();

  // Fetched data
  const [constituency, setConstituency] = useState(null);
  const [villagePlaybooks, setVillagePlaybooks] = useState([]);

  // Village view state
  const [selectedVpIdx, setSelectedVpIdx] = useState(null); // which village playbook is selected in dropdown
  const [villages, setVillages] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [selectedVillage, setSelectedVillage] = useState(0);
  const [search, setSearch] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const detailRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        setLoading(true); setError(null);
        const res = await axiosInstance.get("api/playbook/my-playbook");
        const data = res.data?.data;
        setConstituency(data?.constituency || null);
        setVillagePlaybooks(data?.villagePlaybooks || []);
        setSelectedVpIdx(null);
        setVillages([]); setFiltered([]); setSelectedVillage(0);
      } catch (e) {
        setError(e?.response?.data?.message || "No qualitative survey found for your account.");
      } finally { setLoading(false); }
    })();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // When user picks a village playbook from dropdown
  const handleVpSelect = (idx) => {
    setSelectedVpIdx(idx);
    const vp = villagePlaybooks[idx];
    const vs = vp?.data?.villages || vp?.villages || [];
    setVillages(vs); setFiltered(vs); setSelectedVillage(0); setSearch("");
  };

  useEffect(() => {
    if (!search.trim()) { setFiltered(villages); return; }
    const q = search.toLowerCase();
    setFiltered(villages.filter((v) => {
      const ov = v["overview"] || {};
      return [villageName(v), ov["block"] || "", ov["gram_panchayat"] || ""].some((s) => s.toLowerCase().includes(q));
    }));
    setSelectedVillage(0);
  }, [search, villages]);

  useEffect(() => {
    if (detailRef.current) detailRef.current.scrollTo({ top: 0, behavior: "smooth" });
  }, [selectedVillage, selectedVpIdx]);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    if (open) window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  const inVillageView = selectedVpIdx !== null && villages.length > 0;
  const topbarTitle = constituency?.data?.ac_name || constituency?.name || "Constituency Playbook";

  const bg = isDark ? "#060D1A" : "#F0F6FC";
  const sidebarBg = isDark ? "#192C4A" : "rgba(240,246,252,0.98)";
  const topbarBg = isDark ? "#142240" : "rgba(240,246,252,0.95)";
  const borderCol = isDark ? "rgba(255,255,255,0.15)" : "rgba(0,72,119,0.12)";
  const textPrimary = isDark ? "#F0F6FF" : "#001D30";
  const textMuted = isDark ? "rgba(180,205,240,0.7)" : "#6B7280";
  const textSecondary = isDark ? "rgba(220,235,255,0.88)" : "#374151";
  const activeItemBg = isDark ? "rgba(255,107,26,0.14)" : "rgba(0,72,119,0.07)";
  const searchBg = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,72,119,0.05)";

  const totalVoters = villages.reduce((s, v) => s + (parseInt((v["overview"] || {})["voters"]?.["total"] || 0) || 0), 0);
  console.log("villagePlaybooks",villagePlaybooks)
  console.log("constituency",constituency)
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 3000, background: isDark ? "#060D1A" : "rgba(225,238,250,0.97)", display: "flex", flexDirection: "column" }}>
      {/* ── Topbar ── */}
      <div style={{ height: 60, flexShrink: 0, display: "flex", alignItems: "center", paddingLeft: 20, paddingRight: 20, gap: 14, background: topbarBg, borderBottom: `1px solid ${borderCol}` }}>
        {/* Polstrat logo */}
        <img
          src={polstratLogo}
          alt="Polstrat"
          style={{ height: 32, width: "auto", flexShrink: 0, objectFit: "contain", filter: isDark ? "brightness(1.1)" : "none", zIndex: "999" }}
          onError={(e) => { e.target.style.display = "none"; }}
        />
        <div style={{ flex: 1, zIndex:"999" }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: textPrimary, letterSpacing: "-0.01em" }}>{topbarTitle}</div>
          <div style={{ fontSize: 11, color: textMuted, letterSpacing: "0.04em" }}>
            {inVillageView ? `${villagePlaybooks[selectedVpIdx]?.name || "ग्राम सर्वेक्षण"} · ${filtered.length} गाँव` : "विधानसभा गुणात्मक सर्वेक्षण"}
          </div>
        </div>
        {inVillageView && (
          <button onClick={() => { setSelectedVpIdx(null); setVillages([]); setFiltered([]); }} style={{ fontSize: 12, fontWeight: 700, color: "var(--saffron)", background: "rgba(255,107,26,0.1)", border: "1px solid rgba(255,107,26,0.25)", borderRadius: 20, padding: "5px 14px", cursor: "pointer" }}>
            ← विधानसभा
          </button>
        )}
        <button onClick={onClose} style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, borderRadius: "50%", background: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,72,119,0.08)", border: `1px solid ${isDark ? "rgba(255,255,255,0.2)" : "rgba(0,72,119,0.15)"}`, cursor: "pointer", color: textPrimary }}>
          <X size={16} />
        </button>
      </div>

      {/* ── Body ── */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* ── Sidebar ── */}
        <div style={{ width: 250, flexShrink: 0, display: "flex", flexDirection: "column", background: sidebarBg, borderRight: `1px solid ${borderCol}` }}>
          {/* Village playbook selector */}
          {villagePlaybooks.length > 0 && (
            <div style={{ padding: "12px", borderBottom: `1px solid ${borderCol}`, zIndex:"999" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: textMuted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6  }}>ग्राम सर्वेक्षण चुनें</div>
              <select
                value={selectedVpIdx === null ? "" : selectedVpIdx}
                onChange={(e) => e.target.value === "" ? (setSelectedVpIdx(null), setVillages([]), setFiltered([])) : handleVpSelect(Number(e.target.value))}
                style={{ width: "100%", padding: "8px 10px", borderRadius: 10, fontSize: 13, fontWeight: 600, background: isDark ? "#1A2A42" : "rgba(0,72,119,0.05)", border: `1px solid ${borderCol}`, color: textPrimary, cursor: "pointer", outline: "none" }}
              >
                <option value="" style={{ background: isDark ? "#1A2A42" : "#fff", color: isDark ? "#F0F6FF" : "#001D30" }}>{constituency?.name || "— विधानसभा —"}</option>
                {villagePlaybooks.map((vp, i) => (
                  <option key={i} value={i} style={{ background: isDark ? "#1A2A42" : "#fff", color: isDark ? "#F0F6FF" : "#001D30" }}>{vp.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Village list (only shown when a village playbook is selected) */}
          {inVillageView && (
            <>
              <div style={{ padding: "10px 12px 8px", borderBottom: `1px solid ${borderCol}`, display: "flex", flexDirection: "column", gap: 6, zIndex:"999" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, background: searchBg, border: `1px solid ${borderCol}`, borderRadius: 10, padding: "7px 11px" }}>
                  <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke={textMuted} strokeWidth={2.5}><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
                  <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="गाँव खोजें..." style={{ flex: 1, fontSize: 13, background: "transparent", border: "none", outline: "none", color: textPrimary }} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: textMuted, padding: "2px 2px" }}>
                  <span><strong style={{ color: textSecondary }}>{villages.length}</strong> गाँव</span>
                  <span><strong style={{ color: textSecondary }}>{totalVoters.toLocaleString("hi-IN")}</strong> मत</span>
                </div>
              </div>
              <div className="playbook-village-list" style={{ flex: 1, overflowY: "auto" }}>
                {filtered.map((v, i) => {
                  const isActive = i === selectedVillage;
                  const ov = v["overview"] || {};
                  const vTotal = (ov["voters"] || {})["total"];
                  return (
                    <div key={i} onClick={() => setSelectedVillage(i)} style={{ padding: "11px 14px", cursor: "pointer", borderLeft: `3px solid ${isActive ? "var(--saffron)" : "transparent"}`, borderBottom: "1px solid rgba(255,255,255,0.04)", background: isActive ? activeItemBg : "transparent", transition: "all 0.12s" }}>
                      <div style={{ fontSize: 14, fontWeight: isActive ? 700 : 500, color: isActive ? "var(--saffron)" : textSecondary, lineHeight: 1.4 }}>{villageName(v)}</div>
                      {(!blank(ov["block"]) || !blank(vTotal)) && (
                        <div style={{ fontSize: 11, color: textMuted, marginTop: 2 }}>{[ov["block"], vTotal ? `${vTotal} मत` : ""].filter(Boolean).join(" · ")}</div>
                      )}
                    </div>
                  );
                })}
                {filtered.length === 0 && <div style={{ padding: 24, textAlign: "center", color: textMuted, fontSize: 13 }}>कोई परिणाम नहीं</div>}
              </div>
            </>
          )}

          {/* When no village playbook selected: show AC info summary */}
          {!inVillageView && constituency && (
            <div style={{ flex: 1, padding: "14px 14px", overflowY: "auto", zIndex:"999"}}>
              <div style={{ fontSize: 11, color: textMuted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12 }}>विधानसभा क्षेत्र</div>
              {[["जिला", (constituency?.data || constituency)?.district], ["लोकसभा", (constituency?.data || constituency)?.parliamentary]].filter(([, v]) => !blank(v)).map(([k, v]) => (
                <div key={k} style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: textMuted, marginBottom: 2 }}>{k}</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: textPrimary }}>{v}</div>
                </div>
              ))}
              {villagePlaybooks.length === 0 && (
                <div style={{ marginTop: 20, fontSize: 12, color: textMuted, lineHeight: 1.8, background: searchBg, border: `1px solid ${borderCol}`, borderRadius: 10, padding: "10px 12px" }}>
                  कोई ग्राम रिपोर्ट उपलब्ध नहीं है। Admin से अपलोड करवाएं।
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Detail panel ── */}
        <div ref={detailRef} className="playbook-detail-panel" style={{ flex: 1, overflowY: "auto", overflowX: "hidden", background: bg, position: "relative" }}>
          {isDark && (
            <div style={{ position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none", background: `radial-gradient(ellipse 65% 50% at 20% 15%,rgba(99,102,241,0.09) 0%,transparent 60%),radial-gradient(ellipse 50% 40% at 80% 80%,rgba(255,107,26,0.07) 0%,transparent 55%),linear-gradient(160deg,#07101E 0%,#0C1A2E 55%,#07101E 100%)` }} />
          )}

          <div style={{ position: "relative", zIndex: 1, maxWidth: 1400, margin: "0 auto", padding: "24px 32px" }}>
            {loading && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 400, gap: 16 }}>
                <div style={{ width: 48, height: 48, borderRadius: "50%", border: `3px solid ${isDark ? "rgba(255,107,26,0.15)" : "rgba(0,72,119,0.15)"}`, borderTopColor: "var(--saffron)", animation: "spin 0.8s linear infinite" }} />
                <span style={{ color: textMuted, fontSize: 15 }}>Loading playbook…</span>
              </div>
            )}

            {!loading && error && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 400, gap: 18, textAlign: "center" }}>
                <div style={{ width: 80, height: 80, borderRadius: "50%", background: "radial-gradient(circle,rgba(255,107,26,0.18) 0%,rgba(255,63,136,0.1) 60%,transparent 100%)", border: "1px solid rgba(255,107,26,0.25)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32 }}>📋</div>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: textPrimary, marginBottom: 10 }}>No Qualitative Survey Found</div>
                  <div style={{ fontSize: 15, color: textSecondary, maxWidth: 360, lineHeight: 1.85 }}>{error}</div>
                </div>
              </div>
            )}

            {/* Constituency view */}
            {!loading && !error && !inVillageView && constituency && (
              <ConstituencyView data={constituency} isDark={isDark} />
            )}

            {/* Village detail view */}
            {!loading && !error && inVillageView && filtered.length > 0 && filtered[selectedVillage] && (
              <VillageDetail village={filtered[selectedVillage]} isDark={isDark} />
            )}

            {/* No playbook at all */}
            {!loading && !error && !constituency && villagePlaybooks.length === 0 && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 400, gap: 18, textAlign: "center" }}>
                <div style={{ fontSize: 48 }}>📋</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: textPrimary, marginBottom: 10 }}>No Qualitative Survey Found</div>
                <div style={{ fontSize: 15, color: textSecondary, maxWidth: 360, lineHeight: 1.85 }}>आपके खाते के लिए कोई Qualitative Survey उपलब्ध नहीं है।</div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
